// Where the ⋮ menu goes, as pure arithmetic — its own module so it can be tested without a
// browser, and so the component file keeps exporting only a component (react-refresh).

const MENU_OFFSET = 6;
// Smallest gap between the menu and the edge of the screen.
const MENU_MARGIN = 8;

/**
 * The menu is portalled to `document.body` and positioned `fixed`, so it is NOT clipped by any
 * panel — but nothing constrains it to the screen either. It used to be pinned with
 * `right: window.innerWidth - trigger.right`, i.e. right-aligned to the trigger, and then simply
 * hung off to the left by however much wider than the trigger it was. In Orders' quotes panel on
 * a phone the VIEW pill sits ~117px from the left edge and the menu is 190px wide, so the menu
 * started at -73px and every option lost its first few characters: "Manage" rendered as "…age",
 * "Create Sale Lead" as "Sale Lead" (Alex, 2026-09-16).
 *
 * Right-aligned to the trigger is still the preference — it is what keeps the menu under the ⋮
 * rather than floating off to one side. It is now a preference the screen can overrule.
 *
 * @param {{top: number, bottom: number, right: number}} trigger  viewport rect of the ⋮
 * @returns {{left: number, top: number, maxWidth: number}} all in viewport pixels
 */
export const menuPosition = ({ trigger, menuWidth, menuHeight, viewportWidth, viewportHeight }) => {
  const maxWidth = Math.max(0, viewportWidth - MENU_MARGIN * 2);
  // A menu wider than the screen is clamped by `maxWidth`, so position the width it will actually
  // render at, not the width it asked for.
  const width = Math.min(menuWidth, maxWidth);

  // Preferred: the menu's right edge on the trigger's right edge. Then pushed back inside
  // whichever edge it overhangs — left last, so on a screen narrower than the menu it is the
  // right side that overflows and the labels still start where they are read from.
  let left = trigger.right - width;
  if (left + width > viewportWidth - MENU_MARGIN) left = viewportWidth - MENU_MARGIN - width;
  if (left < MENU_MARGIN) left = MENU_MARGIN;

  // Flip above the trigger only when there is genuinely more room there.
  const spaceBelow = viewportHeight - trigger.bottom;
  const openUpward = spaceBelow < menuHeight + MENU_OFFSET && trigger.top > spaceBelow;
  const top = openUpward
    ? Math.max(MENU_MARGIN, trigger.top - menuHeight - MENU_OFFSET)
    : trigger.bottom + MENU_OFFSET;

  return { left, top, maxWidth };
};

export default menuPosition;
